import React, { useEffect, useMemo, useState } from 'react';
import { api } from '@/lib/api';
import useSEO from '@/lib/useSEO';
import { ArtworkImage } from '@/components/Artwork';
import { Tabs, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Link } from 'react-router-dom';
import { Skeleton } from '@/components/ui/skeleton';

const CATEGORIES = [
    { value: 'all', label: 'All' },
    { value: 'equine', label: 'Equine' },
    { value: 'dog', label: 'Dogs' },
    { value: 'other', label: 'Other' },
];

export default function Gallery() {
    const [items, setItems] = useState(null);
    const [filter, setFilter] = useState('all');
    const [selected, setSelected] = useState(null);
    useSEO({
        title: 'Gallery',
        description: 'A selection of hand-drawn equine and dog portraits by Leiha Martin — graphite and coloured pencil on archival paper.',
        path: '/gallery',
    });

    useEffect(() => {
        api.get('/gallery').then((r) => setItems(r.data));
    }, []);

    const filtered = useMemo(() => {
        if (!items) return null;
        return filter === 'all' ? items : items.filter((i) => i.category === filter);
    }, [items, filter]);

    return (
        <div className="py-10 sm:py-16">
            <div className="mx-auto w-full max-w-6xl px-4 sm:px-6 lg:px-8">
                <div className="max-w-2xl">
                    <p className="caption mb-3">Gallery</p>
                    <h1 className="font-display text-3xl sm:text-5xl text-[color:var(--umber)] leading-tight">Selected portraits.</h1>
                    <p className="mt-4 text-base text-[color:var(--muted-fg)] leading-relaxed">
                        A growing collection of equine, canine and other animal portraits, drawn from
                        treasured photographs.
                    </p>
                </div>
                <div className="mt-7 sm:mt-8 -mx-1 overflow-x-auto" data-testid="gallery-filter-tabs">
                    <Tabs value={filter} onValueChange={setFilter}>
                        <TabsList className="bg-[color:var(--paper-2)] rounded-xl p-1 border border-[color:var(--border)] inline-flex">
                            {CATEGORIES.map((c) => (
                                <TabsTrigger
                                    key={c.value}
                                    value={c.value}
                                    data-testid={`gallery-filter-${c.value}`}
                                    className="data-[state=active]:bg-[color:var(--paper)] data-[state=active]:text-[color:var(--umber)] rounded-lg px-3 sm:px-4"
                                >
                                    {c.label}
                                </TabsTrigger>
                            ))}
                        </TabsList>
                    </Tabs>
                </div>

                <div className="mt-8 sm:mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 sm:gap-6">
                    {filtered === null && (
                        <>
                            {[...Array(6)].map((_, i) => (
                                <Skeleton key={i} className="aspect-[4/5] rounded-2xl bg-[color:var(--paper-2)]" data-testid="loading-state" />
                            ))}
                        </>
                    )}
                    {filtered && filtered.length === 0 && (
                        <div className="col-span-full text-center py-16 sm:py-20 text-[color:var(--muted-fg)]" data-testid="empty-state">
                            No pieces in this category yet.
                        </div>
                    )}
                    {filtered && filtered.map((item) => (
                        <button
                            key={item.id}
                            onClick={() => setSelected(item)}
                            data-testid="gallery-item-card"
                            className="art-frame group text-left transition-shadow hover:shadow-[0_18px_46px_rgba(31,27,24,0.12)] focus:outline-none focus-visible:ring-2 focus-visible:ring-[color:var(--ring)] focus-visible:ring-offset-2 focus-visible:ring-offset-[color:var(--paper)] rounded-2xl"
                        >
                            <div className="art-frame-inner aspect-[4/5]">
                                <ArtworkImage item={item} className="transition-transform duration-500 group-hover:scale-[1.02]" />
                            </div>
                            <div className="flex items-center justify-between px-2 pt-3 pb-1">
                                <p className="font-display text-base text-[color:var(--umber)] truncate pr-2">{item.title || 'Untitled'}</p>
                                <p className="text-xs text-[color:var(--muted-fg)] capitalize whitespace-nowrap">{item.category}</p>
                            </div>
                        </button>
                    ))}
                </div>
            </div>

            <Dialog open={!!selected} onOpenChange={(o) => !o && setSelected(null)}>
                <DialogContent className="max-w-4xl w-[min(96vw,1024px)] bg-[color:var(--paper)] border-[color:var(--border)] p-4 sm:p-6" data-testid="gallery-detail-dialog">
                    <DialogTitle className="sr-only">{selected?.title || 'Artwork detail'}</DialogTitle>
                    {selected && (
                        <div className="grid grid-cols-1 md:grid-cols-5 gap-4 sm:gap-6">
                            <div className="md:col-span-3 art-frame">
                                <div className="art-frame-inner" style={{ maxHeight: '70vh' }}>
                                    <ArtworkImage item={selected} className="max-h-[70vh]" />
                                </div>
                            </div>
                            <div className="md:col-span-2 flex flex-col">
                                <p className="caption">{selected.category}</p>
                                <h3 className="font-display text-2xl text-[color:var(--umber)] mt-2">{selected.title || 'Untitled'}</h3>
                                {selected.medium && (<p className="text-sm text-[color:var(--muted-fg)] mt-2">{selected.medium}</p>)}
                                {selected.size && (<p className="text-sm text-[color:var(--muted-fg)] mt-1">Original size: {selected.size}</p>)}
                                {selected.description && (<p className="text-sm text-[color:var(--graphite)] mt-4 leading-relaxed">{selected.description}</p>)}
                                <div className="mt-6">
                                    <Link to="/commission" data-testid="gallery-detail-commission-cta">
                                        <Button className="rounded-xl bg-[color:var(--umber)] text-[color:var(--paper)] hover:bg-[color:var(--ink)] w-full sm:w-auto">
                                            Commission a portrait
                                        </Button>
                                    </Link>
                                </div>
                            </div>
                        </div>
                    )}
                </DialogContent>
            </Dialog>
        </div>
    );
}
